import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, Building2, Users, Briefcase, ChevronRight, Play,
  HandCoins, ShieldCheck, FileText, Clock, Target, TrendingUp, Scale
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = () => {
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const blogsRes = await axios.get(`${API}/blogs?published_only=true`);
        setBlogs(blogsRes.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  // Schema.org Organization
  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "G Division",
    "url": "https://divisionimmobiliere.com",
    "logo": "https://customer-assets.emergentagent.com/job_beeb63f4-f271-4462-96ad-3cfca96ff85b/artifacts/c5rpsa8q_division%20immobiliere.jpeg",
    "description": "Expert en division immobilière, sortie d'indivision et conseil patrimonial",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "FR"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+33645040975",
      "contactType": "customer service"
    },
    "sameAs": [
      "https://www.youtube.com/@G10visions",
      "https://www.linkedin.com/company/111674288"
    ]
  };

  const services = [
    {
      icon: HandCoins,
      title: "Achat de Votre Bien",
      description: "Nous rachetons votre maison, immeuble ou terrain à un prix juste, sans négociation agressive."
    },
    {
      icon: Building2,
      title: "Division & Valorisation",
      description: "Nous divisons et restructurons le bien pour en révéler tout le potentiel (lots, copropriété, terrains)."
    },
    {
      icon: Users,
      title: "Sortie d'Indivision",
      description: "Indivision bloquée ou succession complexe ? Nous débloquons la situation en rachetant les parts."
    }
  ];

  return (
    <>
      <Helmet>
        <title>G Division | Expert en Division Immobilière et Sortie d'Indivision</title>
        <meta name="description" content="G Division : votre expert en division immobilière, sortie d'indivision et conseil patrimonial. Valorisez votre bien jusqu'à +40%. Étude de faisabilité gratuite." />
        <meta name="keywords" content="division immobilière, G Division, sortie indivision, division terrain, diviser maison, conseil patrimonial, valorisation immobilière" />
        <link rel="canonical" href="https://divisionimmobiliere.com/" />
        
        {/* Open Graph */}
        <meta property="og:title" content="G Division | Expert en Division Immobilière" />
        <meta property="og:description" content="Valorisez votre patrimoine immobilier jusqu'à +40% grâce à la division. Étude de faisabilité gratuite." />
        <meta property="og:url" content="https://divisionimmobiliere.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200" />
        <meta property="og:site_name" content="G Division" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="G Division | Division Immobilière Expert" />
        <meta name="twitter:description" content="Valorisez votre patrimoine jusqu'à +40%. Étude gratuite." />
      </Helmet>

      {/* Schema.org JSON-LD */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />

      <div className="pt-20" data-testid="home-page">
      {/* Hero Section - Modern Premium Design */}
      <section className="relative min-h-[88vh] md:min-h-[92vh] flex items-center overflow-hidden bg-[#0f1d2f]" data-testid="hero-section">
        {/* Background image with strong overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ 
            backgroundImage: `url(https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=1920&q=80)` 
          }}
        />
        {/* Multi-layer gradient overlay for depth */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f1d2f] via-[#0f1d2f]/90 to-[#0f1d2f]/40"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f1d2f] via-transparent to-transparent"></div>

        {/* Decorative gradient orbs */}
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-[#c9a227]/20 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[100px] pointer-events-none"></div>

        {/* Decorative grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}
        ></div>
        
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-20 w-full">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            
            {/* Left: Main content */}
            <div className="lg:col-span-7 xl:col-span-7">
              {/* Trust badge */}
              <div className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-md border border-white/10 rounded-full px-4 py-2 mb-6 md:mb-8 animate-fade-in">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#c9a227] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#c9a227]"></span>
                </span>
                <span className="text-white/90 text-xs md:text-sm font-medium tracking-wide">
                  Marchand de biens · Division immobilière
                </span>
              </div>

              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-6 md:mb-8 animate-fade-in-delay-1">
                Vendez votre bien
                <span className="block bg-gradient-to-r from-[#c9a227] via-[#e6c14d] to-[#c9a227] bg-clip-text text-transparent">
                  à un prix juste
                </span>
                <span className="block text-3xl sm:text-4xl md:text-5xl lg:text-6xl mt-2 text-white/95">
                  sans négociation interminable.
                </span>
              </h1>

              <p className="text-gray-300 text-base md:text-lg lg:text-xl leading-relaxed mb-8 md:mb-10 max-w-xl animate-fade-in-delay-2">
                Nous <strong className="text-white font-semibold">achetons votre bien directement</strong>, puis nous le divisons et le valorisons. Vous touchez un <strong className="text-white font-semibold">prix équitable</strong>, rapidement, sans avancer de frais ni gérer les démarches.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mb-10 md:mb-12 animate-fade-in-delay-3">
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button 
                    className="group bg-[#c9a227] text-white hover:bg-[#b08d22] hover:shadow-[0_0_30px_rgba(201,162,39,0.5)] rounded-xl px-8 py-6 text-sm md:text-base font-semibold w-full sm:w-auto transition-all duration-300"
                    data-testid="hero-sell-btn"
                  >
                    Vendre mon bien à G Division
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                  </Button>
                </Link>
                <Link to="/nos-outils/diagnostic-faisabilite-division" className="w-full sm:w-auto">
                  <Button 
                    variant="outline"
                    className="group border-2 border-white/30 text-white hover:bg-white/10 hover:border-white rounded-xl px-8 py-6 text-sm md:text-base font-semibold bg-white/5 backdrop-blur-sm w-full sm:w-auto transition-all duration-300"
                    data-testid="hero-faisabilite-btn"
                  >
                    <Target className="mr-2" size={18} />
                    Puis-je diviser mon bien&nbsp;? Diagnostic gratuit
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                  </Button>
                </Link>
              </div>

              {/* Trust indicators */}
              <div className="flex flex-wrap items-center gap-6 md:gap-8 text-white/70 animate-fade-in-delay-3">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#c9a227] to-[#8b6f1a] border-2 border-[#0f1d2f]"></div>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-[#0f1d2f]"></div>
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 border-2 border-[#0f1d2f]"></div>
                  </div>
                  <span className="text-sm">La référence expertise en division immobilière</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {[1,2,3,4,5].map(i => (
                    <svg key={i} className="w-4 h-4 text-[#c9a227] fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                  ))}
                  <span className="text-sm ml-1">5.0 / 5</span>
                </div>
              </div>
            </div>

            {/* Right: Stats cards (glass-morphism) */}
            <div className="lg:col-span-5 xl:col-span-5 animate-fade-in-delay-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-5 md:p-6 hover:bg-white/10 hover:border-[#c9a227]/50 transition-all duration-300 col-span-2">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-[#c9a227] text-xs md:text-sm font-semibold uppercase tracking-wider mb-1">Notre savoir-faire</p>
                      <p className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-white leading-tight">Achat &middot; Division &middot; Revente</p>
                    </div>
                    <div className="bg-[#c9a227]/20 rounded-xl p-3">
                      <Building2 className="text-[#c9a227]" size={24} />
                    </div>
                  </div>
                  <p className="text-gray-400 text-xs md:text-sm leading-relaxed">
                    Marchand de biens spécialisé en division immobilière. <span className="text-white">Vous touchez un prix juste</span>, nous prenons en charge tout le travail.
                  </p>
                </div>

                <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-5 hover:bg-white/10 hover:border-[#c9a227]/50 transition-all duration-300">
                  <p className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-white mb-1">200+</p>
                  <p className="text-gray-400 text-xs">Partenaires immobilier &amp; CGP en France</p>
                </div>

                <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-5 hover:bg-white/10 hover:border-[#c9a227]/50 transition-all duration-300">
                  <p className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-white mb-1">4 ans</p>
                  <p className="text-gray-400 text-xs">D'expertise terrain</p>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-white/40 animate-bounce">
            <span className="text-xs uppercase tracking-widest">Découvrir</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3"/></svg>
          </div>
        </div>
      </section>

      {/* Notre Promesse — Marchand de biens positioning */}
      <section className="py-16 md:py-24 bg-white" data-testid="promesse-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12 md:mb-16 max-w-3xl mx-auto">
            <p className="text-[#c9a227] uppercase tracking-[0.2em] text-xs md:text-sm font-semibold mb-4">
              Notre Engagement
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-5 leading-tight">
              On achète, on divise, on revend.
              <span className="block text-[#c9a227] mt-1">Vous, vous encaissez.</span>
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed">
              En tant que <strong className="text-[#1e3a5f]">marchand de biens</strong>, nous achetons directement votre bien à un prix juste. Vous vendez vite, sereinement, sans avancer un centime ni gérer la moindre démarche.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Promesse 1 — Prix juste */}
            <div className="group relative bg-gradient-to-br from-gray-50 to-white border border-gray-100 hover:border-[#c9a227]/40 rounded-2xl p-6 md:p-8 transition-all duration-500 hover:shadow-xl hover:-translate-y-1">
              <div className="absolute top-0 right-0 -mt-3 -mr-3 w-10 h-10 bg-[#c9a227]/10 rounded-full blur-2xl"></div>
              <div className="w-14 h-14 bg-gradient-to-br from-[#c9a227] to-[#8b6f1a] rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-[#c9a227]/20 group-hover:scale-105 transition-transform duration-300">
                <HandCoins className="text-white" size={26} />
              </div>
              <span className="inline-block text-xs font-bold text-[#c9a227] uppercase tracking-[0.15em] mb-2">01 · Prix juste</span>
              <h3 className="font-['Playfair_Display'] text-xl md:text-2xl font-bold text-[#1e3a5f] mb-3">
                Pas de négociation agressive
              </h3>
              <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                Notre signature&nbsp;: <strong className="text-[#1e3a5f]">acheter à un prix juste</strong> basé sur la valeur réelle du marché, sans négociation interminable. Vous repartez avec un prix équitable, rapidement et sereinement.
              </p>
            </div>

            {/* Promesse 2 — Conditions transparentes */}
            <div className="group relative bg-gradient-to-br from-gray-50 to-white border border-gray-100 hover:border-[#1e3a5f]/40 rounded-2xl p-6 md:p-8 transition-all duration-500 hover:shadow-xl hover:-translate-y-1">
              <div className="absolute top-0 right-0 -mt-3 -mr-3 w-10 h-10 bg-blue-500/10 rounded-full blur-2xl"></div>
              <div className="w-14 h-14 bg-gradient-to-br from-[#1e3a5f] to-[#0f1d2f] rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-[#1e3a5f]/20 group-hover:scale-105 transition-transform duration-300">
                <FileText className="text-[#c9a227]" size={26} />
              </div>
              <span className="inline-block text-xs font-bold text-[#1e3a5f] uppercase tracking-[0.15em] mb-2">02 · Transparence</span>
              <h3 className="font-['Playfair_Display'] text-xl md:text-2xl font-bold text-[#1e3a5f] mb-3">
                Conditions suspensives claires
              </h3>
              <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                Nous protégeons les deux parties avec des <strong className="text-[#1e3a5f]">clauses suspensives transparentes</strong>&nbsp;: PLU, urbanisme, financement, autorisations. Tout est écrit noir sur blanc. Aucune mauvaise surprise.
              </p>
            </div>

            {/* Promesse 3 — Zéro frais avancés */}
            <div className="group relative bg-gradient-to-br from-gray-50 to-white border border-gray-100 hover:border-emerald-400/40 rounded-2xl p-6 md:p-8 transition-all duration-500 hover:shadow-xl hover:-translate-y-1">
              <div className="absolute top-0 right-0 -mt-3 -mr-3 w-10 h-10 bg-emerald-500/10 rounded-full blur-2xl"></div>
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-emerald-500/20 group-hover:scale-105 transition-transform duration-300">
                <ShieldCheck className="text-white" size={26} />
              </div>
              <span className="inline-block text-xs font-bold text-emerald-600 uppercase tracking-[0.15em] mb-2">03 · Zéro frais</span>
              <h3 className="font-['Playfair_Display'] text-xl md:text-2xl font-bold text-[#1e3a5f] mb-3">
                Vous n'avancez pas un centime
              </h3>
              <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                Notaire, géomètre, urbanisme, travaux, mise en copropriété, revente des lots… <strong className="text-[#1e3a5f]">nous prenons tout en charge</strong>. Vous signez, vous encaissez, on s'occupe du reste.
              </p>
            </div>
          </div>

          {/* CTA bar — Diagnostic Faisabilité */}
          <div className="mt-12 md:mt-16 bg-gradient-to-r from-[#1e3a5f] to-[#0f1d2f] rounded-3xl p-6 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 text-white overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#c9a227]/10 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl pointer-events-none"></div>
            <div className="relative">
              <p className="text-[#c9a227] text-xs font-bold uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
                <Target size={14} />
                Résultat en 3 minutes · 100% gratuit
              </p>
              <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold mb-1">
                Puis-je diviser mon bien&nbsp;?
              </h3>
              <p className="text-gray-300 text-sm md:text-base">
                Obtenez votre score de faisabilité 0-100, un verdict argumenté et une estimation du gain potentiel.
              </p>
            </div>
            <Link to="/nos-outils/diagnostic-faisabilite-division" className="relative w-full md:w-auto">
              <Button 
                className="group bg-[#c9a227] hover:bg-[#b08d22] text-white px-8 py-6 text-base font-semibold rounded-xl shadow-lg shadow-[#c9a227]/30 hover:shadow-[0_0_30px_rgba(201,162,39,0.5)] transition-all duration-300 w-full md:w-auto"
                data-testid="promesse-faisabilite-btn"
              >
                Lancer mon diagnostic
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-[#f8f9fa] border-t border-gray-100" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-[#c9a227] uppercase tracking-[0.2em] text-xs md:text-sm font-semibold mb-4">
              Nos Expertises
            </p>
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4">
              Des Solutions Sur Mesure
            </h2>
            <div className="section-separator mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-white border border-gray-100 p-6 md:p-8 card-hover shadow-sm"
                data-testid={`service-card-${index}`}
              >
                <div className="w-14 h-14 md:w-16 md:h-16 bg-[#1e3a5f] rounded-sm flex items-center justify-center mb-4 md:mb-6">
                  <service.icon className="text-[#c9a227]" size={24} />
                </div>
                <h3 className="font-['Playfair_Display'] text-lg md:text-xl font-semibold text-[#1e3a5f] mb-3 md:mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm md:text-base">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="py-16 md:py-24 bg-white" data-testid="about-preview-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="img-zoom order-2 lg:order-1">
              <img 
                src="https://images.unsplash.com/photo-1752159400890-d906038f1b35?w=800"
                alt="G Division Team"
                className="w-full h-[300px] md:h-[400px] object-cover shadow-lg"
              />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-[#c9a227] uppercase tracking-[0.2em] text-xs md:text-sm font-semibold mb-4">
                À Propos
              </p>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4 md:mb-6">
                Qui Sommes-Nous ?
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4 md:mb-6 text-sm md:text-base">
                G Division est une agence spécialisée dans la division immobilière et l'accompagnement 
                des propriétaires confrontés à des situations d'indivision complexes. Notre expertise 
                nous permet de transformer des biens bloqués en véritables opportunités.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6 md:mb-8 text-sm md:text-base">
                Que vous soyez propriétaire, investisseur ou marchand de biens, nous mettons notre 
                savoir-faire à votre service pour optimiser votre patrimoine immobilier.
              </p>
              <Link to="/qui-sommes-nous">
                <Button 
                  className="bg-[#1e3a5f] text-white hover:bg-[#162a45] rounded-none px-6 md:px-8 py-3 uppercase tracking-wider text-xs md:text-sm font-semibold"
                  data-testid="about-cta-btn"
                >
                  En Savoir Plus
                  <ChevronRight className="ml-2" size={16} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Tools Preview - Modern Design */}
      <section className="py-16 md:py-24 bg-white" data-testid="tools-preview-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-[#c9a227] uppercase tracking-[0.2em] text-xs md:text-sm font-semibold mb-4">
              Ressources Gratuites
            </p>
            <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4">
              Nos Outils Pour Vous
            </h2>
            <div className="section-separator mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Tool 1 - Copropriété */}
            <Link 
              to="/nos-outils/gestion-copropriete"
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 p-1 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl"
              data-testid="tool-preview-copro"
            >
              <div className="relative h-full rounded-xl bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 p-6 md:p-8">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Building2 className="text-white" size={32} />
                    </div>
                    <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full">
                      GRATUIT
                    </span>
                  </div>
                  
                  <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-white mb-3">
                    Gestion de Copropriété
                  </h3>
                  <p className="text-blue-100 text-sm md:text-base leading-relaxed mb-6">
                    L'application idéale pour les syndics bénévoles. Gérez votre copropriété simplement, sans expertise requise.
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-white/80 text-sm">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                      Disponible sur Play Store
                    </div>
                    <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-white/20 transition-colors">
                      <ArrowRight className="text-white group-hover:translate-x-1 transition-transform" size={18} />
                    </div>
                  </div>
                </div>
              </div>
            </Link>

            {/* Tool 2 - Calculateur */}
            <Link 
              to="/nos-outils/calculateur-rentabilite"
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 p-1 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl"
              data-testid="tool-preview-calc"
            >
              <div className="relative h-full rounded-xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 p-6 md:p-8">
                {/* Decorative elements */}
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Briefcase className="text-white" size={32} />
                    </div>
                    <span className="bg-white/20 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1 rounded-full">
                      EN LIGNE
                    </span>
                  </div>
                  
                  <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-white mb-3">
                    Calculateur Rentabilité
                  </h3>
                  <p className="text-emerald-100 text-sm md:text-base leading-relaxed mb-6">
                    Analysez la rentabilité de vos investissements immobiliers. Rendement brut, net, cash-flow en quelques clics.
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-white/80 text-sm">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                      Outil intégré
                    </div>
                    <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-white/20 transition-colors">
                      <ArrowRight className="text-white group-hover:translate-x-1 transition-transform" size={18} />
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>

          <div className="text-center mt-10">
            <Link to="/nos-outils">
              <Button 
                variant="outline"
                className="border-2 border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white rounded-none px-6 md:px-8 py-3 uppercase tracking-wider text-xs md:text-sm font-semibold"
                data-testid="all-tools-btn"
              >
                Découvrir Tous Nos Outils
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 md:py-24 bg-[#1e3a5f]" data-testid="video-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <p className="text-[#c9a227] uppercase tracking-[0.2em] text-xs md:text-sm font-semibold mb-4">
                Chaîne YouTube
              </p>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6">
                Découvrez Nos Conseils en Vidéo
              </h2>
              <p className="text-gray-300 leading-relaxed mb-6 md:mb-8 text-sm md:text-base">
                Retrouvez sur notre chaîne YouTube des conseils pratiques sur la division immobilière, 
                les marchands de biens et l'investissement immobilier.
              </p>
              <a 
                href="https://www.youtube.com/@G10visions"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button 
                  className="bg-[#c9a227] text-white hover:bg-[#b08d22] rounded-none px-6 md:px-8 py-3 uppercase tracking-wider text-xs md:text-sm font-semibold"
                  data-testid="youtube-channel-btn"
                >
                  <Play className="mr-2" size={16} />
                  Voir la Chaîne
                </Button>
              </a>
            </div>
            <div className="order-1 lg:order-2 aspect-video w-full rounded-sm overflow-hidden shadow-2xl border-4 border-white/10">
              <iframe
                src="https://www.youtube.com/embed/-2i2Yhe9HqU"
                title="G Division YouTube"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* CGP / Investor Banner - Modern */}
      <section className="relative py-12 md:py-16 bg-gradient-to-br from-[#0f1d2f] via-[#1e3a5f] to-[#0f1d2f] overflow-hidden" data-testid="cgp-banner-section">
        {/* Decorative orb */}
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-96 h-96 bg-[#c9a227]/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute top-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 md:px-8">
          <Link
            to="/nos-biens"
            className="group flex flex-col md:flex-row items-stretch md:items-center gap-6 md:gap-10 bg-white/[0.03] backdrop-blur-md border border-white/10 hover:border-[#c9a227]/50 rounded-3xl p-6 md:p-8 transition-all duration-500 hover:bg-white/[0.06] hover:shadow-[0_0_40px_rgba(201,162,39,0.15)]"
            data-testid="cgp-investor-banner"
          >
            {/* Icon block */}
            <div className="flex-shrink-0 relative">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br from-[#c9a227] to-[#8b6f1a] rounded-2xl flex items-center justify-center shadow-lg shadow-[#c9a227]/30 group-hover:scale-105 transition-transform duration-500">
                <Briefcase className="text-white" size={32} />
              </div>
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-400 rounded-full border-2 border-[#0f1d2f] flex items-center justify-center">
                <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
              </div>
            </div>

            {/* Text content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[#c9a227] text-xs font-bold uppercase tracking-[0.2em]">CGP &amp; Investisseurs</span>
                <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider">Nouveau</span>
              </div>
              <h3 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold text-white mb-2 leading-tight">
                Accédez à nos <span className="text-[#c9a227]">opportunités exclusives</span>
              </h3>
              <p className="text-gray-300 text-sm md:text-base leading-relaxed max-w-2xl">
                Biens issus de nos divisions, rendements optimisés, accompagnement clé en main. Sélection rigoureuse pour conseillers en gestion de patrimoine et investisseurs.
              </p>
            </div>

            {/* CTA arrow */}
            <div className="flex md:flex-col items-center gap-3 flex-shrink-0">
              <div className="hidden md:flex flex-col items-end text-right">
                <span className="text-white text-sm font-semibold">Voir les biens</span>
                <span className="text-gray-400 text-xs">Rendements jusqu'à 8%</span>
              </div>
              <div className="w-14 h-14 bg-[#c9a227] rounded-full flex items-center justify-center group-hover:bg-white group-hover:scale-110 transition-all duration-300 shadow-lg shadow-[#c9a227]/30">
                <ArrowRight className="text-white group-hover:text-[#1e3a5f] group-hover:translate-x-1 transition-all duration-300" size={22} />
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* Blog Preview */}
      {blogs.length > 0 && (
        <section className="py-16 md:py-24 bg-white" data-testid="blog-preview-section">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12 md:mb-16">
              <p className="text-[#c9a227] uppercase tracking-[0.2em] text-xs md:text-sm font-semibold mb-4">
                Blog
              </p>
              <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4">
                Nos Derniers Articles
              </h2>
              <div className="section-separator mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
              {blogs.map((blog, index) => (
                <Link 
                  key={blog.id}
                  to={`/blog/${blog.slug}`}
                  className="group blog-card bg-white border border-gray-100 overflow-hidden"
                  data-testid={`blog-preview-${index}`}
                >
                  <div className="img-zoom">
                    <img 
                      src={blog.image_url || 'https://images.unsplash.com/photo-1657129208507-7005dfc5b36a?w=800'}
                      alt={blog.title}
                      className="w-full h-40 md:h-48 object-cover"
                    />
                  </div>
                  <div className="p-4 md:p-6">
                    <span className="text-[#c9a227] text-xs uppercase tracking-wider">
                      {blog.category}
                    </span>
                    <h3 className="font-['Playfair_Display'] text-base md:text-lg font-semibold text-[#1e3a5f] mt-2 mb-2 md:mb-3 group-hover:text-[#c9a227] transition-colors line-clamp-2">
                      {blog.title}
                    </h3>
                    <p className="text-gray-600 text-xs md:text-sm leading-relaxed line-clamp-2">
                      {blog.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-8 md:mt-12">
              <Link to="/blog">
                <Button 
                  variant="outline"
                  className="border-2 border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white rounded-none px-6 md:px-8 py-3 uppercase tracking-wider text-xs md:text-sm font-semibold"
                  data-testid="all-blogs-btn"
                >
                  Voir Tous les Articles
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Final CTA — Diagnostic Faisabilité (replaces appointment / phone CTAs) */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-[#0f1d2f] via-[#1e3a5f] to-[#0f1d2f] overflow-hidden" data-testid="cta-section">
        {/* Decorative orbs */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#c9a227]/15 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-amber-500/10 rounded-full blur-[100px] pointer-events-none"></div>
        <div 
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
            backgroundSize: '60px 60px'
          }}
        ></div>

        <div className="relative max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 bg-[#c9a227]/15 border border-[#c9a227]/30 text-[#c9a227] text-xs font-bold uppercase tracking-[0.2em] px-4 py-2 rounded-full mb-6">
              <Target size={14} />
              Diagnostic gratuit · 3 minutes
            </span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
              Puis-je diviser mon bien&nbsp;?
              <span className="block bg-gradient-to-r from-[#c9a227] via-[#e6c14d] to-[#c9a227] bg-clip-text text-transparent mt-2">
                Obtenez la réponse en 3 minutes.
              </span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              Répondez à 8 questions simples. Notre moteur analyse votre bien, votre PLU et vos contraintes, puis vous délivre un score de faisabilité, un verdict argumenté et une estimation du gain potentiel.
            </p>
          </div>

          {/* 3 benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10">
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-5 text-center hover:bg-white/10 hover:border-[#c9a227]/40 transition-all duration-300">
              <div className="w-12 h-12 mx-auto bg-[#c9a227]/15 rounded-xl flex items-center justify-center mb-3">
                <Scale className="text-[#c9a227]" size={22} />
              </div>
              <p className="text-white font-semibold text-sm mb-1">Score 0-100</p>
              <p className="text-gray-400 text-xs">Faisabilité évaluée objectivement</p>
            </div>
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-5 text-center hover:bg-white/10 hover:border-[#c9a227]/40 transition-all duration-300">
              <div className="w-12 h-12 mx-auto bg-[#c9a227]/15 rounded-xl flex items-center justify-center mb-3">
                <TrendingUp className="text-[#c9a227]" size={22} />
              </div>
              <p className="text-white font-semibold text-sm mb-1">Gain estimé</p>
              <p className="text-gray-400 text-xs">+5 à +45 % sur votre patrimoine</p>
            </div>
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-5 text-center hover:bg-white/10 hover:border-[#c9a227]/40 transition-all duration-300">
              <div className="w-12 h-12 mx-auto bg-[#c9a227]/15 rounded-xl flex items-center justify-center mb-3">
                <ShieldCheck className="text-[#c9a227]" size={22} />
              </div>
              <p className="text-white font-semibold text-sm mb-1">Sans engagement</p>
              <p className="text-gray-400 text-xs">100 % gratuit, 100 % anonyme</p>
            </div>
          </div>

          <div className="flex justify-center">
            <Link to="/nos-outils/diagnostic-faisabilite-division" className="w-full sm:w-auto">
              <Button 
                className="group bg-[#c9a227] text-white hover:bg-[#b08d22] hover:shadow-[0_0_40px_rgba(201,162,39,0.6)] rounded-xl px-10 py-7 text-base md:text-lg font-semibold w-full sm:w-auto transition-all duration-300"
                data-testid="cta-faisabilite-btn"
              >
                <Target className="mr-2" size={20} />
                Lancer mon diagnostic gratuit
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Home;
